import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Building2, Globe, Clock, FileText, AlertTriangle, Download } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { AuditLogViewer } from "@/components/settings/AuditLogViewer";

const INDUSTRIES = [
  { value: 'technology', label: 'Technologie & Software' },
  { value: 'finance', label: 'Finanzdienstleistungen' },
  { value: 'healthcare', label: 'Gesundheitswesen' },
  { value: 'manufacturing', label: 'Produktion & Fertigung' },
  { value: 'retail', label: 'Einzelhandel & E-Commerce' },
  { value: 'consulting', label: 'Beratung & Professional Services' },
  { value: 'education', label: 'Bildung & Training' },
  { value: 'real_estate', label: 'Immobilien' },
  { value: 'marketing', label: 'Marketing & Werbung' },
  { value: 'logistics', label: 'Logistik & Transport' },
  { value: 'hospitality', label: 'Gastgewerbe & Tourismus' },
  { value: 'energy', label: 'Energie & Umwelt' },
  { value: 'other', label: 'Andere' },
];

const TIMEZONES = [
  { value: 'Europe/Berlin', label: 'Berlin (MEZ/MESZ)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (MEZ/MESZ)' },
  { value: 'Europe/Vienna', label: 'Wien (MEZ/MESZ)' },
  { value: 'Europe/Zurich', label: 'Zürich (MEZ/MESZ)' },
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Singapore', label: 'Singapur (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
];

export default function Workspace() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspace, loading: workspaceLoading } = useWorkspace();
  const [isSaving, setIsSaving] = useState(false);
  const [isExportingData, setIsExportingData] = useState(false);
  const [isDeletingWorkspace, setIsDeletingWorkspace] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const isOwner = workspace?.created_by === user?.id;
  
  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    industry: '',
    description: '',
    timezone: 'Europe/Berlin',
  });

  useEffect(() => {
    if (workspace) {
      setFormData({
        name: workspace.name || '',
        domain: workspace.domain || '',
        industry: workspace.industry || '',
        description: workspace.description || '',
        timezone: workspace.timezone || 'Europe/Berlin',
      });
    }
  }, [workspace]);

  const handleSave = async () => {
    if (!workspace) return;

    if (!formData.name.trim()) {
      toast.error("Workspace-Name ist erforderlich");
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("workspaces")
        .update({
          name: formData.name.trim(),
          domain: formData.domain.trim() || null,
          industry: formData.industry || null,
          description: formData.description.trim() || null,
          timezone: formData.timezone,
        })
        .eq("id", workspace.id);

      if (error) throw error;

      toast.success("Workspace-Einstellungen erfolgreich gespeichert");
    } catch (error: any) {
      console.error("Error saving workspace:", error);
      toast.error("Einstellungen konnten nicht gespeichert werden");
    } finally {
      setIsSaving(false);
    }
  };

  // Export all workspace data
  const handleExportWorkspaceData = async () => {
    if (!workspace) return;

    setIsExportingData(true);
    try {
      // Fetch all workspace-related data
      const [sessionsData, membersData, activitiesData] = await Promise.all([
        supabase.from("decision_sessions").select("*").eq("workspace_id", workspace.id),
        supabase.from("workspace_members").select("*, profiles(email, name)").eq("workspace_id", workspace.id),
        supabase.from("activity_logs").select("*").eq("workspace_id", workspace.id).order("created_at", { ascending: false }).limit(500),
      ]);

      const exportData = {
        export_date: new Date().toISOString(),
        workspace: {
          id: workspace.id,
          name: workspace.name,
          slug: workspace.slug,
          domain: workspace.domain,
          industry: workspace.industry,
          description: workspace.description,
          plan_type: workspace.plan_type,
          created_at: workspace.created_at,
        },
        sessions: sessionsData.data || [],
        members: membersData.data?.map((m: any) => ({
          ...m,
          profiles: undefined,
          email: m.profiles?.email,
          name: m.profiles?.name,
        })) || [],
        recent_activities: activitiesData.data || [],
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${workspace.slug}-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Workspace-Daten wurden erfolgreich exportiert");
    } catch (error: any) {
      console.error("Export error:", error);
      toast.error("Daten konnten nicht exportiert werden");
    } finally {
      setIsExportingData(false);
    }
  };

  // Delete workspace (owner only)
  const handleDeleteWorkspace = async () => {
    if (!workspace || !isOwner) return;

    if (deleteConfirmation !== workspace.name) {
      toast.error("Bitte gib den exakten Workspace-Namen ein");
      return;
    }

    setIsDeletingWorkspace(true);
    try {
      // Delete all related data first
      await Promise.all([
        supabase.from("decision_sessions").delete().eq("workspace_id", workspace.id),
        supabase.from("activity_logs").delete().eq("workspace_id", workspace.id),
        supabase.from("notifications").delete().eq("workspace_id", workspace.id),
        supabase.from("workspace_invites").delete().eq("workspace_id", workspace.id),
      ]);

      // Delete workspace members
      await supabase.from("workspace_members").delete().eq("workspace_id", workspace.id);

      // Finally delete the workspace
      const { error } = await supabase.from("workspaces").delete().eq("id", workspace.id);
      if (error) throw error;

      toast.success("Workspace wurde erfolgreich gelöscht");
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Delete error:", error);
      toast.error("Workspace konnte nicht gelöscht werden: " + (error.message || "Unbekannter Fehler"));
    } finally {
      setIsDeletingWorkspace(false);
    }
  };

  if (workspaceLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-muted-foreground">Kein Workspace gefunden</p>
        <Button onClick={() => navigate('/dashboard')}>
          Zum Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8 max-w-4xl pb-20 md:pb-6">

      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Basis-Informationen
          </CardTitle>
          <CardDescription>
            Grundlegende Details über deinen Workspace
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace-Name *</Label>
            <Input
              id="workspace-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="z.B. Mein Unternehmen"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-domain">Domain (optional)</Label>
            <Input
              id="workspace-domain"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="z.B. meinunternehmen.de"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-industry">Branche</Label>
            <Select 
              value={formData.industry} 
              onValueChange={(value) => setFormData({ ...formData, industry: value })}
            >
              <SelectTrigger id="workspace-industry">
                <SelectValue placeholder="Branche auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((ind) => (
                  <SelectItem key={ind.value} value={ind.value}>
                    {ind.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="workspace-description">
              <FileText className="h-4 w-4 inline mr-1" />
              Beschreibung (optional)
            </Label>
            <Textarea
              id="workspace-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Kurze Beschreibung des Workspace..."
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {formData.description.length} / 500 Zeichen
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Regional Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Regionale Einstellungen
          </CardTitle>
          <CardDescription>
            Zeitzone und lokale Einstellungen
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-timezone">
              <Clock className="h-4 w-4 inline mr-1" />
              Zeitzone
            </Label>
            <Select 
              value={formData.timezone} 
              onValueChange={(value) => setFormData({ ...formData, timezone: value })}
            >
              <SelectTrigger id="workspace-timezone">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <Button 
          onClick={handleSave} 
          disabled={isSaving || !formData.name.trim()}
        >
          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Änderungen speichern
        </Button>
      </div>

      {/* Audit Log Viewer */}
      <AuditLogViewer workspaceId={workspace.id} />

      {/* Danger Zone */}
      <Card className="border-destructive">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Gefährliche Zone</CardTitle>
          </div>
          <CardDescription>
            Diese Aktionen können nicht rückgängig gemacht werden
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Export Data */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border rounded-lg">
            <div className="space-y-1">
              <h4 className="font-medium">Daten exportieren</h4>
              <p className="text-sm text-muted-foreground">
                Exportiere alle Workspace-Daten als JSON-Datei
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleExportWorkspaceData}
              disabled={isExportingData}
            >
              {isExportingData ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Exportieren
            </Button>
          </div>

          {/* Delete Workspace - Owner Only */}
          {isOwner && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border border-destructive/50 rounded-lg bg-destructive/5">
              <div className="space-y-1">
                <h4 className="font-medium text-destructive">Workspace löschen</h4>
                <p className="text-sm text-muted-foreground">
                  Der Workspace und alle zugehörigen Daten werden unwiderruflich gelöscht
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    Workspace löschen
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Workspace endgültig löschen?</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-4">
                      <p>
                        Diese Aktion kann nicht rückgängig gemacht werden. Der Workspace 
                        <strong className="text-foreground"> "{workspace.name}"</strong> und alle 
                        zugehörigen Daten werden dauerhaft gelöscht.
                      </p>
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Warnung</AlertTitle>
                        <AlertDescription>
                          Alle Entscheidungen, Teammitglieder, Aktivitätsprotokolle und 
                          Einstellungen werden unwiderruflich entfernt.
                        </AlertDescription>
                      </Alert>
                      <div className="space-y-2">
                        <Label htmlFor="delete-confirm">
                          Gib zur Bestätigung <strong>{workspace.name}</strong> ein:
                        </Label>
                        <Input
                          id="delete-confirm"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          placeholder={workspace.name}
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setDeleteConfirmation("")}>
                      Abbrechen
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteWorkspace}
                      disabled={deleteConfirmation !== workspace.name || isDeletingWorkspace}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {isDeletingWorkspace ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      Endgültig löschen
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
